import { useId } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const GLOW_ICON_MAP = {
  "stat-glow-green": "stat-icon-green",
  "stat-glow-red": "stat-icon-purple",
  "stat-glow-indigo": "stat-icon-indigo",
  "stat-glow-amber": "stat-icon-amber",
  "stat-glow-sky": "stat-icon-sky",
  "stat-glow-blue": "stat-icon-sky",
};

const formatPct = (pct) => {
  const num = Number(pct);
  if (Number.isNaN(num)) return "0%";
  return Number.isInteger(num) ? `${num}%` : `${num.toFixed(1)}%`;
};

const clickableProps = (onClick, label) => {
  if (typeof onClick !== "function") return {};
  return {
    role: "button",
    tabIndex: 0,
    onClick,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    },
    "aria-label": label,
  };
};

const CardShell = ({
  title,
  icon: Icon,
  glowClass,
  delay,
  className,
  testId,
  children,
}) => {
  const iconClass = GLOW_ICON_MAP[glowClass] ?? "stat-icon-sky";

  return (
    <Card
      className={cn("card-hover animate-fade-in flex h-full min-w-0 flex-col", delay, glowClass, className)}
      data-testid={testId}
    >
      <CardContent className="flex h-full flex-1 flex-col px-3 pb-2 pt-2.5">
        <div className="mb-0.5 flex items-start justify-between gap-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {title}
          </p>
          {Icon && (
            <div className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-md", iconClass)}>
              <Icon className="h-3 w-3" />
            </div>
          )}
        </div>
        {children}
      </CardContent>
    </Card>
  );
};

const MetricTile = ({
  value,
  label,
  variant = "light",
  onClick,
  loading,
  testId,
  percentage,
  active = false,
  compact = false,
}) => {
  const isDark = variant === "dark";
  const isClickable = typeof onClick === "function";
  return (
    <div
      className={cn(
        "flex min-h-0 min-w-0 flex-1 flex-col justify-center rounded-2xl px-2.5 py-1.5",
        isDark ? "bg-[#1A1A2E] text-white" : "bg-muted/70 text-foreground",
        isClickable &&
          "cursor-pointer select-none transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active && "ring-2 ring-[var(--xl-primary)]",
      )}
      data-testid={testId}
      aria-pressed={isClickable ? active : undefined}
      {...clickableProps(onClick, `View ${label}`)}
    >
      <p className={cn(
        "font-bold leading-none tracking-tight tabular-nums",
        compact ? "text-xl" : "text-2xl",
      )}>
        {loading ? "—" : value}
        {!loading && !compact && percentage != null && percentage !== "" && (
          <span className={cn("ml-1 text-sm font-medium", isDark ? "text-white/50" : "text-muted-foreground")}>
            ({formatPct(percentage)})
          </span>
        )}
      </p>
      <p className={cn("mt-1 text-[11px] font-medium truncate", isDark ? "text-white/60" : "text-muted-foreground")}>
        {label}
        {!loading && compact && percentage != null && percentage !== "" ? ` · ${formatPct(percentage)}` : ""}
      </p>
    </div>
  );
};

const TickDial = ({ value, activeTicks, loading, caption, color = "#0E9F6E", fill = false }) => {
  const ticks = 32;
  const lit = loading ? 0 : Math.min(ticks, Math.max(0, activeTicks));
  const cx = 60;
  const cy = 60;

  return (
    <div
      className={cn(
        "relative mx-auto",
        fill ? "aspect-square h-full max-h-full w-auto max-w-full" : "w-full max-w-[108px]",
      )}
    >
      <svg
        viewBox="0 0 120 120"
        className={cn("h-auto w-full", fill && "h-full max-h-full")}
        aria-hidden
      >
        {Array.from({ length: ticks }, (_, i) => {
          const angle = ((i / ticks) * 360 - 90) * (Math.PI / 180);
          const x1 = cx + Math.cos(angle) * 42;
          const y1 = cy + Math.sin(angle) * 42;
          const x2 = cx + Math.cos(angle) * 54;
          const y2 = cy + Math.sin(angle) * 54;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={i < lit ? color : "#E4E4EA"}
              strokeWidth={i < lit ? 3.2 : 2.2}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={cn(
            "font-bold leading-none tracking-tight tabular-nums",
            fill ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl",
          )}
        >
          {loading ? "—" : value}
        </span>
        {caption && (
          <span className="mt-1 text-[11px] font-medium text-muted-foreground">{caption}</span>
        )}
      </div>
    </div>
  );
};

const CompletionRing = ({
  completed,
  total,
  loading,
  color = "#4338CA",
  caption = "complete",
  centerValue,
}) => {
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const ratio = !loading && total > 0 ? Math.min(1, Number(completed) / Number(total)) : 0;
  const pct = Math.round(ratio * 100);

  return (
    <div className="relative mx-auto w-full max-w-[108px]">
      <svg viewBox="0 0 120 120" className="h-auto w-full" aria-hidden>
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#E4E4EA" strokeWidth="8" />
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${circumference * ratio} ${circumference}`}
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold leading-none tabular-nums sm:text-2xl">
          {loading ? "—" : (centerValue ?? `${pct}%`)}
        </span>
        {caption && (
          <span className="mt-1 text-[11px] font-medium text-muted-foreground">{caption}</span>
        )}
      </div>
    </div>
  );
};

export const StudiesCreativeCard = ({
  value = 0,
  loading = false,
  delay = "",
  icon,
  glowClass = "stat-glow-green",
  className,
}) => {
  const total = Number(value) || 0;
  const activeTicks = loading ? 0 : Math.min(32, total);

  return (
    <CardShell
      title="Studies"
      icon={icon}
      glowClass={glowClass}
      delay={delay}
      className={className}
      testId="dashboard-stat-studies"
    >
      <div className="flex min-h-0 flex-1 items-start justify-center">
        <TickDial
          value={value}
          activeTicks={activeTicks}
          loading={loading}
          caption="total studies"
        />
      </div>
    </CardShell>
  );
};

export const PatientsCreativeCard = ({
  total = 0,
  pending = 0,
  completed = 0,
  loading = false,
  delay = "",
  icon,
  glowClass = "stat-glow-indigo",
  onTotalClick,
  onPendingClick,
  onCompletedClick,
  className,
}) => (
  <CardShell
    title="Patients"
    icon={icon}
    glowClass={glowClass}
    delay={delay}
    className={className}
    testId="dashboard-stat-patients"
  >
    <div className="flex min-h-0 flex-1 items-start justify-center">
      <CompletionRing completed={completed} total={total} loading={loading} />
    </div>

    <div className="mt-auto grid grid-cols-3 gap-1.5 pt-1">
      <MetricTile
        value={total}
        label="total"
        loading={loading}
        onClick={onTotalClick}
        testId="dashboard-stat-item-patients"
      />
      <MetricTile
        value={completed}
        label="completed"
        loading={loading}
        onClick={onCompletedClick}
        testId="dashboard-stat-item-completed_patients"
      />
      <MetricTile
        value={pending}
        label="pending"
        variant="dark"
        loading={loading}
        onClick={onPendingClick}
        testId="dashboard-stat-item-pending_patients"
      />
    </div>
  </CardShell>
);

const HatchedColon = () => {
  const uid = useId().replace(/:/g, "");
  const hatchId = `ct-ratio-hatch-${uid}`;

  return (
    <svg
      viewBox="0 0 18 64"
      className="h-14 w-4 shrink-0 sm:h-16 sm:w-[1.125rem]"
      aria-hidden
    >
      <defs>
        <pattern
          id={hatchId}
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(42)"
        >
          <line x1="0" y1="0" x2="0" y2="6" stroke="#C58A12" strokeWidth="2.2" />
        </pattern>
      </defs>
      <rect width="18" height="64" rx="9" fill={`url(#${hatchId})`} />
      <circle cx="9" cy="20" r="3.2" fill="#1A1A2E" />
      <circle cx="9" cy="44" r="3.2" fill="#1A1A2E" />
    </svg>
  );
};

export const CtRatioCreativeCard = ({
  images = 0,
  reports = 0,
  loading = false,
  delay = "",
  icon: Icon,
  className,
}) => {
  const imagesCount = Number(images) || 0;
  const reportsCount = Number(reports) || 0;

  return (
    <Card
      className={cn(
        "card-hover animate-fade-in h-full min-w-0 overflow-hidden border-0 shadow-none",
        delay,
        className,
      )}
      data-testid="dashboard-stat-ct-ratio"
    >
      <CardContent className="flex h-full flex-col bg-[#F6E6C4] p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-bold tracking-tight text-[#1A1A2E] sm:text-base">
            CT Ratio
          </h3>
          {Icon && (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1A1A2E] text-white">
              <Icon className="h-3 w-3" aria-hidden />
            </div>
          )}
        </div>

        <div
          className="flex min-h-0 flex-1 flex-col items-center justify-center"
          data-testid="dashboard-stat-item-ct_pending_report"
          aria-label={`${imagesCount} images to ${reportsCount} reports`}
        >
          <div className="flex items-center justify-center gap-3">
            <span
              className="min-w-0 truncate text-3xl font-bold leading-none tracking-tight text-[#1A1A2E] tabular-nums sm:text-4xl"
              data-testid="dashboard-stat-item-ct_images_patients"
            >
              {loading ? "—" : imagesCount}
            </span>
            <HatchedColon />
            <span
              className="min-w-0 truncate text-3xl font-bold leading-none tracking-tight text-[#1A1A2E] tabular-nums sm:text-4xl"
              data-testid="dashboard-stat-item-ct_report_patients"
            >
              {loading ? "—" : reportsCount}
            </span>
          </div>
          <p className="mt-3 text-[11px] font-medium text-[#8A6A2A]">
            images : reports
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export const NetworkCreativeCard = ({
  hospitals = 0,
  doctors = 0,
  loading = false,
  delay = "",
  icon,
  glowClass = "stat-glow-red",
  onHospitalsClick,
  onDoctorsClick,
  className,
}) => (
    <CardShell
      title="Network"
      icon={icon}
      glowClass={glowClass}
      delay={delay}
      className={className}
      testId="dashboard-stat-network"
    >
      <div className="mt-0.5 flex flex-1 flex-col gap-1.5">
        <div
          className="flex min-h-0 flex-1 cursor-pointer flex-col justify-center rounded-2xl bg-muted/70 px-3 py-2 transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="dashboard-stat-item-organizations"
          {...clickableProps(onHospitalsClick, "View hospitals")}
        >
          <p className="text-[11px] font-medium text-muted-foreground">Hospitals</p>
          <p className="mt-1 text-3xl font-bold leading-none tabular-nums">{loading ? "—" : hospitals}</p>
        </div>
        <div
          className="flex min-h-0 flex-1 cursor-pointer flex-col justify-center rounded-2xl bg-[#1A1A2E] px-3 py-2 text-white transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="dashboard-stat-item-doctors"
          {...clickableProps(onDoctorsClick, "View doctors")}
        >
          <p className="text-[11px] font-medium text-white/60">Doctors</p>
          <p className="mt-1 text-3xl font-bold leading-none tabular-nums">{loading ? "—" : doctors}</p>
        </div>
      </div>
    </CardShell>
);

const SemiGauge = ({ value, percentage, loading, color = "#0E9F6E", caption }) => {
  const cx = 70;
  const cy = 72;
  const r = 52;
  const stroke = 10;
  const arcLen = Math.PI * r;
  const ratio = loading ? 0 : Math.min(1, Math.max(0, Number(percentage) / 100));
  const progressLen = arcLen * ratio;

  return (
    <div className="relative mx-auto my-auto w-full max-w-[140px]">
      <svg viewBox="0 0 140 92" className="h-auto w-full" aria-hidden>
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="#E4E4EA"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${progressLen} ${arcLen}`}
        />
      </svg>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center">
        <span className="text-3xl font-bold leading-none tracking-tight tabular-nums">
          {loading ? "—" : value}
        </span>
        {caption && (
          <span className="mt-1 text-[11px] font-medium text-muted-foreground">{caption}</span>
        )}
      </div>
    </div>
  );
};

export const ReportsCreativeCard = ({
  total = 0,
  hospitals = 0,
  loading = false,
  delay = "",
  icon,
  glowClass = "stat-glow-sky",
  className,
}) => {
  const totalNum = Number(total) || 0;
  const activeTicks = loading ? 0 : Math.min(32, totalNum);

  return (
    <CardShell
      title="Reports"
      icon={icon}
      glowClass={glowClass}
      delay={delay}
      className={className}
      testId="report-stat-reports"
    >
      <div className="relative min-h-[7.5rem] flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <TickDial
            value={total}
            activeTicks={activeTicks}
            loading={loading}
            caption="total reports"
            color="#0284C7"
            fill
          />
        </div>
        <div
          className="absolute bottom-0 left-0 z-10 leading-none"
          data-testid="report-stat-item-hospitals"
        >
          <p className="text-lg font-bold tabular-nums sm:text-xl">
            {loading ? "—" : hospitals}
          </p>
          <p className="mt-0.5 text-[11px] font-medium text-muted-foreground">hospitals</p>
        </div>
      </div>
    </CardShell>
  );
};

export const GenderCreativeCard = ({
  femaleCount = 0,
  femalePercentage = 0,
  maleCount = 0,
  malePercentage = 0,
  femaleActive = false,
  maleActive = false,
  onFemaleClick,
  onMaleClick,
  loading = false,
  delay = "",
  icon,
  glowClass = "stat-glow-amber",
  className,
}) => {
  return (
    <CardShell
      title="Gender"
      icon={icon}
      glowClass={glowClass}
      delay={delay}
      className={className}
      testId="report-stat-gender"
    >
      <div className="mt-1 flex flex-1 flex-col gap-1.5">
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col justify-center rounded-2xl bg-muted/70 px-3 py-2 transition-transform",
            typeof onFemaleClick === "function" &&
              "cursor-pointer hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            femaleActive && "ring-2 ring-[var(--xl-primary)]",
          )}
          data-testid="report-stat-item-female"
          aria-pressed={typeof onFemaleClick === "function" ? femaleActive : undefined}
          {...clickableProps(onFemaleClick, "View female")}
        >
          <p className="text-[11px] font-medium text-muted-foreground">Female</p>
          <p className="mt-1 text-3xl font-bold leading-none tabular-nums">
            {loading ? "—" : femaleCount}
            {!loading && (
              <span className="ml-1.5 text-sm font-medium text-muted-foreground">
                ({formatPct(femalePercentage)})
              </span>
            )}
          </p>
        </div>
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col justify-center rounded-2xl bg-[#1A1A2E] px-3 py-2 text-white transition-transform",
            typeof onMaleClick === "function" &&
              "cursor-pointer hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            maleActive && "ring-2 ring-[var(--xl-primary)]",
          )}
          data-testid="report-stat-item-male"
          aria-pressed={typeof onMaleClick === "function" ? maleActive : undefined}
          {...clickableProps(onMaleClick, "View male")}
        >
          <p className="text-[11px] font-medium text-white/60">Male</p>
          <p className="mt-1 text-3xl font-bold leading-none tabular-nums">
            {loading ? "—" : maleCount}
            {!loading && (
              <span className="ml-1.5 text-sm font-medium text-white/50">
                ({formatPct(malePercentage)})
              </span>
            )}
          </p>
        </div>
      </div>
    </CardShell>
  );
};

const ANATOMY_BAR_COLORS = {
  tricuspid: "#B5F19E",
  bicuspid: "#B4A9F2",
  viv: "#7DD3FC",
  tav_tav: "#FBBF24",
};

const ANATOMY_BAR_FALLBACK_COLORS = ["#B5F19E", "#B4A9F2", "#7DD3FC", "#FBBF24"];

const anatomyBarColor = (item, index) =>
  item?.color
  ?? ANATOMY_BAR_COLORS[item?.key]
  ?? ANATOMY_BAR_FALLBACK_COLORS[index % ANATOMY_BAR_FALLBACK_COLORS.length];

const AnatomyStackedBar = ({ items = [], loading = false }) => {
  const segments = items
    .map((item, index) => ({
      key: item.key ?? item.title ?? index,
      value: Number(item.value) || 0,
      title: item.title,
      color: anatomyBarColor(item, index),
    }))
    .filter((segment) => segment.value > 0);
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const hasData = !loading && total > 0;

  return (
    <div
      className="flex h-full min-h-[8.5rem] w-[4.25rem] shrink-0 flex-col-reverse gap-1.5 sm:w-[4.75rem]"
      aria-hidden
    >
      {hasData ? (
        segments.map((segment) => (
          <div
            key={segment.key}
            className="w-full rounded-[1.05rem]"
            style={{
              flex: `${segment.value} 1 0`,
              minHeight: "0.75rem",
              backgroundColor: segment.color,
            }}
            title={`${segment.title}: ${segment.value}`}
          />
        ))
      ) : (
        <div className="h-full w-full rounded-[1.05rem] bg-muted" />
      )}
    </div>
  );
};

export const AnatomyCreativeCard = ({
  items = [],
  loading = false,
  delay = "",
  icon,
  glowClass = "stat-glow-indigo",
  className,
}) => {
  return (
    <CardShell
      title="Anatomy"
      icon={icon}
      glowClass={glowClass}
      delay={delay}
      className={className}
      testId="report-stat-anatomy"
    >
      <div className="flex min-h-0 flex-1 items-stretch justify-between gap-3">
        <div className="flex min-w-0 flex-col justify-end gap-2.5 pb-0.5">
          {items.map((item, index) => {
            const isClickable = typeof item.onClick === "function";
            const color = anatomyBarColor(item, index);
            return (
              <div
                key={item.key ?? item.title}
                className={cn(
                  "flex items-center gap-2 rounded-lg py-0.5 pr-1",
                  isClickable &&
                    "cursor-pointer select-none transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  item.active && "bg-muted/80 ring-2 ring-[var(--xl-primary)]",
                )}
                data-testid={item.key ? `report-stat-item-${item.key}` : undefined}
                aria-pressed={isClickable ? Boolean(item.active) : undefined}
                {...clickableProps(item.onClick, `View ${item.title}`)}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                  aria-hidden
                />
                <span className="whitespace-nowrap text-sm font-medium text-muted-foreground">
                  {item.title}
                </span>
                <span className="whitespace-nowrap text-lg font-bold leading-none tabular-nums">
                  {loading ? "—" : item.value}
                  {!loading && item.percentage != null && item.percentage !== "" && (
                    <span className="ml-1.5 text-xs font-medium text-muted-foreground">
                      {formatPct(item.percentage)}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <AnatomyStackedBar items={items} loading={loading} />
      </div>
    </CardShell>
  );
};

export const SmallAnnulusCreativeCard = ({
  value = 0,
  percentage = 0,
  active = false,
  onClick,
  loading = false,
  delay = "",
  icon,
  glowClass = "stat-glow-green",
  className,
}) => {
  return (
    <CardShell
      title="Small Annulus"
      icon={icon}
      glowClass={glowClass}
      delay={delay}
      className={cn(typeof onClick === "function" && "cursor-pointer", className)}
      testId="report-stat-small-annulus"
    >
      <div
        className={cn(
          "flex flex-1 items-center rounded-2xl",
          active && "ring-2 ring-[var(--xl-primary)]",
        )}
        {...clickableProps(onClick, "Filter small annulus reports")}
        aria-pressed={typeof onClick === "function" ? active : undefined}
      >
        <SemiGauge
          value={value}
          percentage={percentage}
          loading={loading}
          caption={loading ? undefined : `${formatPct(percentage)} of reports`}
        />
      </div>
    </CardShell>
  );
};
